import { useState } from 'react';

export interface FilterGroup {
  key: string;
  label: string;
  options: string[];
}

interface FilterPanelProps {
  groups: FilterGroup[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  sortOptions: { key: string; label: string }[];
  sortValue: string;
  onSortChange: (value: string) => void;
  onReset: () => void;
  resultCount: number;
  showSort?: boolean;
}

function PillRow({
  label,
  options,
  value,
  onSelect,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onSelect: (option: string) => void;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 py-3 border-b border-background-200 last:border-b-0">
      <span className="text-sm text-foreground-500 sm:w-16 shrink-0 sm:pt-1">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onSelect(option.value)}
              className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors ${
                active
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterPanel({
  groups,
  values,
  onChange,
  sortOptions,
  sortValue,
  onSortChange,
  onReset,
  resultCount,
  showSort = true,
}: FilterPanelProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg bg-background-50 border border-background-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 md:hidden">
        <span className="text-sm text-foreground-600">
          共 <span className="font-semibold text-primary-600">{resultCount}</span> 部作品
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-background-100 text-sm text-foreground-700 cursor-pointer"
        >
          <i className="ri-equalizer-line"></i>
          筛选
          <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} cursor-pointer`}></i>
        </button>
      </div>

      <div className={`${open ? 'block' : 'hidden'} md:block px-4 pb-4 md:py-4`}>
        {groups.map((group) => (
          <PillRow
            key={group.key}
            label={group.label}
            options={group.options.map((option) => ({ value: option, label: option }))}
            value={values[group.key] ?? group.options[0]}
            onSelect={(option) => onChange(group.key, option)}
          />
        ))}

        {showSort && (
          <PillRow
            label="排序"
            options={sortOptions.map((option) => ({ value: option.key, label: option.label }))}
            value={sortValue}
            onSelect={onSortChange}
          />
        )}

        <div className="flex items-center justify-between pt-4 mt-1 border-t border-background-200">
          <span className="hidden md:inline text-sm text-foreground-600">
            共 <span className="font-semibold text-primary-600">{resultCount}</span> 部作品
          </span>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-foreground-600 hover:text-accent-600 hover:bg-accent-50 cursor-pointer transition-colors"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}